<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { Settings } from "lucide-vue-next";
import { CHART_TYPE_OPTIONS } from "./constants";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useSurveyStore } from "@stores/survey/survey";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/episto-ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const store = useResultsV2Store();
const { currentView } = storeToRefs(store);

const globalChartTypeOptions = CHART_TYPE_OPTIONS.filter((o) => o.value !== "nps");

const localesStore = useSurveyLocalesStore();
const { localeOptions } = storeToRefs(localesStore);

const surveyStore = useSurveyStore();
const { areWeightsEnabled } = storeToRefs(surveyStore);

const globalOptions = computed(() => currentView.value?.options || {});

function updateGlobalOption(key, value) {
  if (!currentView.value) return;
  currentView.value.options = {
    ...globalOptions.value,
    [key]: value,
  };
  store.markViewDirty();
}

const language = computed({
  get: () => globalOptions.value.locale_token || null,
  set: (val) => updateGlobalOption("locale_token", val),
});

const defaultChartType = computed({
  get: () => globalOptions.value.chart_type || null,
  set: (val) => updateGlobalOption("chart_type", val),
});


const showSignificance = computed({
  get: () => globalOptions.value.show_significance ?? true,
  set: (val) => updateGlobalOption("show_significance", val),
});

const applyWeightings = computed({
  get: () => globalOptions.value.apply_weightings ?? true,
  set: (val) => updateGlobalOption("apply_weightings", val),
});


const defaultDisplay = computed({
  get: () => globalOptions.value.default_display || null,
  set: (val) => updateGlobalOption("default_display", val),
});

const displayOptions = [
  { value: "chart", label: "Chart" },
  { value: "table", label: "Table" },
];

const layout = computed({
  get: () => globalOptions.value.layout || null,
  set: (val) => updateGlobalOption("layout", val),
});

const layoutOptions = [
  { value: "list", label: "List" },
  { value: "grid", label: "Grid" },
];

</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button variant="outline" class="text-13 font-medium shadow-xs">
        <Settings class="mr-1 h-3.5 w-3.5 text-muted-foreground" />
        Settings
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" class="w-72">
      <div class="space-y-4">
        <h4 class="text-13 font-semibold pb-4">Settings</h4>

        <div class="space-y-2">
          <Label>Language</Label>
          <Select
            :model-value="language"
            :options="localeOptions"
            placeholder="Default"
            clearable
            @update:model-value="language = $event" />
        </div>

        <div class="space-y-2">
          <Label>Default display</Label>
          <Select
            :model-value="defaultDisplay"
            :options="displayOptions"
            placeholder="Chart"
            clearable
            @update:model-value="defaultDisplay = $event" />
        </div>

        <div class="space-y-2">
          <Label>View default layout</Label>
          <Select
            :model-value="layout"
            :options="layoutOptions"
            placeholder="List"
            clearable
            @update:model-value="layout = $event" />
        </div>

        <div class="space-y-2">
          <Label>Default chart type</Label>
          <Select
            :model-value="defaultChartType"
            :options="globalChartTypeOptions"
            placeholder="Auto"
            clearable
            @update:model-value="defaultChartType = $event" />
        </div>

        <div class="flex items-center justify-between">
          <Label class="cursor-pointer">Show significance</Label>
          <Switch :model-value="showSignificance" @update:model-value="showSignificance = $event" />
        </div>

        <div v-if="areWeightsEnabled" class="flex items-center justify-between">
          <Label class="cursor-pointer">Apply weightings</Label>
          <Switch :model-value="applyWeightings" @update:model-value="applyWeightings = $event" />
        </div>

      </div>
    </PopoverContent>
  </Popover>
</template>
